// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { APIService } from "../../utils/apiService";
import { AppConfig } from "../../config/config";
import { HttpStatusCode } from "axios";

const initialState: PaymentMethodState = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  paymentMethods: null,
};

interface PaymentMethodState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  paymentMethods: PaymentMethod[] | null;
}

interface PaymentMethodInterface {
  count: number;
  paymentMethods: PaymentMethod[];
}

export interface PaymentMethod {
  id: number;
  name: string;
  netSuitePaymentFileFormat: string;
}

export const fetchPaymentMethods = createAsyncThunk(
  "paymentMethods/fetchPaymentMethods",
  async (_, { dispatch }) => {
    return new Promise<{ paymentMethods: PaymentMethodInterface }>(
      (resolve, reject) => {
        APIService.getInstance()
          .get(AppConfig.serviceUrls.paymentMethods)
          .then((resp) => {
            if (resp.status === HttpStatusCode.Ok) {
              resolve({ paymentMethods: resp.data });
            } else {
              reject("Oops, Something went wrong!");
            }
          })
          .catch((error: Error) => {
            reject(error);
          });
      }
    );
  }
);

export const PaymentMethodSlice = createSlice({
  name: "fetchPaymentMethods",
  initialState,
  reducers: {
    updateStateMessage: (state, action: PayloadAction<string>) => {
      state.stateMessage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.state = "loading";
        state.stateMessage = "Loading Payment Methods";
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.state = "success";
        state.stateMessage = null;
        state.paymentMethods = action.payload.paymentMethods.paymentMethods;
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.state = "failed";
        state.errorMessage = action.error.message
          ? action.error.message
          : "Oops, Something went wrong!";
      });
  },
});

export const { updateStateMessage } = PaymentMethodSlice.actions;

export default PaymentMethodSlice.reducer;
